import { Col, Row, Card, Table } from "antd";
import React from "react";
import { adjEntryWithCard, adjEntryWithoutCard } from "../../AllOrgActivities/AdjSpldHelper";
import I18n from "../../../common/I18n";

const TABLE_KEY = "linehaul_bound_adjustments";

const COLUMN_WIDTH_MAP = {
  type: 120,
  warehouses: 260,
  charge_type: 160,
  charge_value: 140,
  min: 80,
  max: 80,
};

const COLUMN_TITLE_MAP = {
  type: "Type",
  warehouses: I18n.t('general.warehouses'),
  charge_type: "Charge Type",
  charge_value: "Charge Value",
  min: "Min",
  max: "Max",
};

const extractLHIOColumns = (tableData = []) => {
  if (!tableData || tableData.length === 0) return [];
  return Object.keys(tableData[0]).filter(k => k !== "id");
};

const buildColumnDef = (key, highlightMap = null) => {
  return {
    title: COLUMN_TITLE_MAP[key] || key,
    dataIndex: key,
    key,
    width: COLUMN_WIDTH_MAP[key] || 120,
    render: (value, record) => {
      const isHighlighted = highlightMap && highlightMap[record.id] && highlightMap[record.id][key];

      const displayValue =
        key === "warehouses"
          ? Array.isArray(value)
            ? value.join(", ")
            : value || "—"
          : value !== undefined && value !== ""
          ? value
          : "—";

      if (isHighlighted) {
        return (
          <span
            style={{
              background: "#fffbe6",
              border: "1px solid #ffe58f",
              borderRadius: "3px",
              padding: "1px 6px",
              fontWeight: 600,
              color: "#d46b08",
            }}
          >
            {displayValue}
          </span>
        );
      }

      return <span>{displayValue}</span>;
    },
  };
};

const buildHighlightMap = (oldRows = [], newRows = []) => {
  const map = {};
  newRows.forEach(newRow => {
    const oldRow = oldRows.find(r => r.id === newRow.id);
    if (!oldRow) {
      map[newRow.id] = Object.keys(newRow)
        .filter(k => k !== "id")
        .reduce((acc, k) => ({ ...acc, [k]: true }), {});
    } else {
      map[newRow.id] = {};
      Object.keys(newRow)
        .filter(k => k !== "id")
        .forEach(k => {
          const oldVal = Array.isArray(oldRow[k]) ? JSON.stringify(oldRow[k]) : oldRow[k];
          const newVal = Array.isArray(newRow[k]) ? JSON.stringify(newRow[k]) : newRow[k];
          if (oldVal !== newVal) map[newRow.id][k] = true;
        });
    }
  });
  return map;
};

/* ─────────────────────────────────────────────────────────
   Main Component
   ───────────────────────────────────────────────────────── */
export const AdjLHIORate = ({ data }) => {
  const rawData = data || {};
  const diffData = rawData?.difference?.[0] ? rawData.difference[0] : {};

  const accAdjData = !_.isEmpty(diffData)
    ? _.compact(_.flatten(diffData?.account_adjustments ?? []))
    : [];

  const adjConfig =
    !_.isEmpty(diffData) && diffData?.adjustment_configuration
      ? diffData.adjustment_configuration
      : [];

  /* ── derived flags ── */
  const accAdjAction = accAdjData?.[0]?.action ?? "";
  const adjConfigAction = adjConfig?.[0]?.action ?? "";
  const accAdjType = accAdjAction === "DELETE" ? "REMOVED" : "ADDED";
  const adjConfigType = adjConfigAction === "DELETE" ? "REMOVED" : "ADDED";

  const oldTableData = adjConfig?.[0]?.old_value?.[TABLE_KEY] ?? [];
  const newTableData = adjConfig?.[0]?.new_value?.[TABLE_KEY] ?? [];

  const isOldAdjConfigPresent = !_.isEmpty(adjConfig?.[0]?.old_value);
  const isNewAdjConfigPresent = !_.isEmpty(adjConfig?.[0]?.new_value);
  const isOldTableDataPresent = !_.isEmpty(oldTableData);
  const isNewTableDataPresent = !_.isEmpty(newTableData);

  /**
   * RULE:
   * - account_adjustments[0] has BOTH old_value AND new_value
   *     → card metadata (Code / Name / Category) changed
   *     → show Previous card + Updated card ONLY — no table
   *
   * - otherwise (account_adjustments only has new_value/old_value, or is empty)
   *     → change is in adjustment_configuration table rows
   *     → show Previous table + Updated table — no adj card
   */
  const accAdjEntry = accAdjData?.[0] ?? {};
  const hasAccAdjMetaChange =
    !_.isEmpty(accAdjEntry?.old_value) && !_.isEmpty(accAdjEntry?.new_value);

  const highlightMap = buildHighlightMap(oldTableData, newTableData);

  const oldColumns = extractLHIOColumns(oldTableData).map(col => buildColumnDef(col));
  const newColumns = extractLHIOColumns(newTableData).map(col => buildColumnDef(col, highlightMap));

  /* ── shared card style ── */
  const cardStyle = {
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.09)",
    marginBottom: "16px",
    padding: "2px",
  };

  const tableProps = (columns, dataSource, extraRowClassName) => ({
    columns,
    dataSource,
    pagination: false,
    bordered: true,
    scroll: { x: "max-content", y: "calc(100vh - 550px)" },
    tableLayout: "fixed",
    rowClassName: extraRowClassName,
  });

  /* ── render NEW / ADDED entry (non-UPDATE action) ── */
  const renderRegularGroup = () => {
    if (rawData.action === "UPDATE") return null;

    const tableData =
      adjConfig?.[0]?.new_value?.[TABLE_KEY] ?? adjConfig?.[0]?.old_value?.[TABLE_KEY] ?? [];

    const accAdjTitle = adjConfig?.[0]?.new_value ? "New Entry" : "Old Entry";
    const columns = extractLHIOColumns(tableData).map(col => buildColumnDef(col));

    return (
      <div className="mb-6">
        <h4
          className="ant-typography ant-typography-md font-medium mb-3"
          style={{ textAlign: "left", fontSize: "16px" }}
        >
          {accAdjTitle}
        </h4>
        <Card style={cardStyle}>
          {/* show adj card if metadata changed, else skip it */}
          {hasAccAdjMetaChange &&
            adjEntryWithCard(adjConfig, "", adjConfigType, adjConfigAction, "LHIO")}
          {hasAccAdjMetaChange && adjEntryWithoutCard(accAdjData, "", accAdjType, accAdjAction)}
          {/* show table only when it is the thing that changed */}
          {!hasAccAdjMetaChange && !_.isEmpty(tableData) && (
            <Table
              {...tableProps(
                columns,
                tableData.map((row, i) => ({ ...row, key: row.id || i }))
              )}
            />
          )}
        </Card>
      </div>
    );
  };

  /* ── render MODIFIED entry (UPDATE action) ── */
  const renderModifiedGroup = () => {
    if (rawData.action !== "UPDATE") return null;

    return (
      <div className="mb-6">
        <h4
          className="ant-typography ant-typography-md font-medium mb-3"
          style={{ textAlign: "left", fontSize: "16px" }}
        >
          Modified Entries
        </h4>

        {/* ── Previous Values ── */}
        <h5
          className="ant-typography ant-typography-sm font-medium mb-2"
          style={{ textAlign: "left", color: "#666", fontWeight: "bold" }}
        >
          Previous Values
        </h5>
        <div className="mb-4">
          <Card style={cardStyle}>
            {/*
              account_adjustments[0] has both old_value + new_value
              → metadata (Code/Name/Category) changed → show adj cards only
            */}
            {hasAccAdjMetaChange &&
              isOldAdjConfigPresent &&
              adjEntryWithCard(adjConfig, "", adjConfigType, "previous", "LHIO")}
            {hasAccAdjMetaChange && adjEntryWithoutCard(accAdjData, "", accAdjType, "previous")}

            {/*
              No metadata change → adjustment_configuration table rows changed
              → show previous table rows only
            */}
            {!hasAccAdjMetaChange && isOldTableDataPresent && (
              <Table
                {...tableProps(
                  oldColumns,
                  oldTableData.map((row, i) => ({ ...row, key: row.id || i }))
                )}
              />
            )}
          </Card>
        </div>

        {/* ── Updated Values ── */}
        <h5
          className="ant-typography ant-typography-sm font-medium mb-2"
          style={{ textAlign: "left", color: "#666", fontWeight: "bold" }}
        >
          Updated Values
        </h5>
        <div className="mb-4">
          <Card style={cardStyle}>
            {/* metadata changed → show adj cards only */}
            {hasAccAdjMetaChange &&
              isNewAdjConfigPresent &&
              adjEntryWithCard(adjConfig, "", adjConfigType, "new", "LHIO")}
            {hasAccAdjMetaChange && adjEntryWithoutCard(accAdjData, "", accAdjType, "new")}

            {/* table rows changed → show new table only */}
            {!hasAccAdjMetaChange && isNewTableDataPresent && (
              <Table
                {...tableProps(
                  newColumns,
                  newTableData.map((row, i) => ({ ...row, key: row.id || i })),
                  record => {
                    const oldRow = oldTableData.find(r => r.id === record.id);
                    return !oldRow ? "lhio-new-row" : "";
                  }
                )}
              />
            )}
          </Card>
        </div>
      </div>
    );
  };

  /* ── root render ── */
  return (
    <>
      <style>{`
        .lhio-new-row td {
          background: #f6ffed !important;
        }
      `}</style>
      <Row gutter={[16, 32]}>
        <Col span={24}>
          <div className="mt-6">
            <span style={{ fontSize: "20px", color: "#395484", fontWeight: 500 }}>
              LHIO Rate Adjustment
            </span>
            <div className="mt-6">
              {renderRegularGroup()}
              {renderModifiedGroup()}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};
